<template>
  <div class="sm2-keys-panel" v-loading="loading">
    <el-card class="workspace-card toolbar-card" shadow="hover">
      <div class="page-toolbar">
        <div>
          <div class="toolbar-title">SM2 密钥管理</div>
          <div class="toolbar-desc">查看当前 SM2 公钥、私钥和生成时间；新增时同步 CN / US 两台服务器。</div>
        </div>
        <div class="toolbar-actions">
          <el-button :icon="Refresh" :loading="loading" @click="loadInfo">刷新</el-button>
          <el-button type="primary" :icon="Plus" :loading="adding" @click="addKey">新增一组</el-button>
        </div>
      </div>
    </el-card>

    <div class="summary-grid">
      <div class="summary-item">
        <span>有效组数</span>
        <strong>{{ validCountText }}</strong>
      </div>
      <div class="summary-item">
        <span>失效保留</span>
        <strong>{{ invalidRetentionHours }} 小时</strong>
      </div>
      <div class="summary-item">
        <span>当前 Redis</span>
        <strong>{{ redisDb }}</strong>
      </div>
      <div class="summary-item wide">
        <span>Key 前缀</span>
        <strong>{{ keyPrefix }}</strong>
      </div>
    </div>

    <el-card class="workspace-card table-card" shadow="hover">
      <template #header>
        <div class="card-header">
          <span>密钥列表</span>
          <small>{{ idsSetKey }} · {{ historySetKey }}</small>
        </div>
      </template>

      <el-table
        :data="items"
        row-key="id"
        border
        stripe
        empty-text="暂无 SM2 密钥"
        class="sm2-table"
      >
        <el-table-column label="状态" width="92" fixed>
          <template #default="{ row }">
            <el-tag :type="row.valid ? 'success' : 'info'" effect="light">
              {{ row.valid ? '有效' : '失效' }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column prop="id" label="编号 / 版本" min-width="270" show-overflow-tooltip>
          <template #default="{ row }">
            <span class="mono strong-text">{{ row.id }}</span>
          </template>
        </el-table-column>

        <el-table-column prop="created_time" label="生成时间" width="170" />
        <el-table-column prop="invalid_time" label="失效时间" width="170">
          <template #default="{ row }">{{ row.invalid_time || '-' }}</template>
        </el-table-column>
        <el-table-column prop="delete_time" label="删除时间" width="170">
          <template #default="{ row }">{{ row.delete_time || '-' }}</template>
        </el-table-column>

        <el-table-column label="公钥" min-width="430">
          <template #default="{ row }">
            <div class="key-cell">
              <code>{{ row.public_key }}</code>
              <el-button
                :icon="CopyDocument"
                circle
                size="small"
                title="复制公钥"
                @click.stop="copyValue(row.public_key, '公钥')"
              />
            </div>
          </template>
        </el-table-column>

        <el-table-column label="私钥" min-width="330">
          <template #default="{ row }">
            <div class="key-cell">
              <code>{{ row.private_key }}</code>
              <el-button
                :icon="CopyDocument"
                circle
                size="small"
                title="复制私钥"
                @click.stop="copyValue(row.private_key, '私钥')"
              />
            </div>
          </template>
        </el-table-column>
      </el-table>
    </el-card>
  </div>
</template>

<script setup>
import { CopyDocument, Plus, Refresh } from '@element-plus/icons-vue';
import { useMazdaSm2Keys } from '@/composables/mazda/useMazdaSm2Keys';

const props = defineProps({
  mazdaPost: { type: Function, required: true },
  serverName: { type: String, default: '' },
});

const {
  loading,
  adding,
  items,
  validCountText,
  invalidRetentionHours,
  redisDb,
  idsSetKey,
  historySetKey,
  keyPrefix,
  loadInfo,
  addKey,
  copyValue,
} = useMazdaSm2Keys(props.mazdaPost);
</script>

<style scoped>
.sm2-keys-panel {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.workspace-card {
  border-radius: 12px;
  border: 1px solid rgba(226, 233, 243, 0.95);
  overflow: hidden;
}

.page-toolbar,
.toolbar-actions,
.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.toolbar-title,
.card-header > span {
  font-weight: 800;
  color: #1f2d3d;
  font-size: 16px;
}

.toolbar-desc {
  margin-top: 6px;
  color: #7c8798;
  font-size: 13px;
  line-height: 1.55;
}

.card-header small {
  color: #8a94a6;
  font-size: 12px;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(160px, 1fr));
  gap: 12px;
}

.summary-item {
  min-width: 0;
  padding: 13px 14px;
  border-radius: 10px;
  border: 1px solid #e8eef8;
  background: linear-gradient(180deg, #ffffff, #f7faff);
  display: flex;
  flex-direction: column;
  gap: 8px;
  box-shadow: 0 8px 18px rgba(26, 46, 79, 0.05);
}

.summary-item span {
  color: #8a94a6;
  font-size: 12px;
}

.summary-item strong {
  color: #1f2d3d;
  font-size: 15px;
  word-break: break-all;
}

.mono,
.key-cell code {
  font-family: Consolas, Monaco, 'Courier New', monospace;
}

.strong-text {
  font-weight: 700;
  color: #28364a;
}

.key-cell {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 32px;
  align-items: start;
  gap: 8px;
}

.key-cell code {
  display: block;
  min-width: 0;
  padding: 7px 8px;
  border-radius: 8px;
  border: 1px solid #e5edf7;
  background: #f8fbff;
  color: #2d3b4f;
  font-size: 12px;
  line-height: 1.45;
  white-space: normal;
  overflow-wrap: anywhere;
}

.sm2-table :deep(.el-button.is-circle) {
  width: 30px;
  height: 30px;
  padding: 0;
}

@media (max-width: 1180px) {
  .summary-grid {
    grid-template-columns: repeat(2, minmax(160px, 1fr));
  }
}

@media (max-width: 720px) {
  .summary-grid {
    grid-template-columns: 1fr;
  }
}
</style>
